import { z } from 'zod'
import { timestampSchema } from './common.js'
import { scopeSchema } from './enums.js'

export const apiTokenSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  prefix: z.string(),
  createdAt: timestampSchema,
  lastUsedAt: timestampSchema.nullable(),
})
export type ApiTokenSummary = z.infer<typeof apiTokenSummarySchema>

export const createApiTokenInputSchema = z.object({
  name: z.string().min(1),
})
export type CreateApiTokenInput = z.infer<typeof createApiTokenInputSchema>

export const createApiTokenResponseSchema = z.object({
  id: z.string(),
  name: z.string(),
  prefix: z.string(),
  createdAt: timestampSchema,
  /** Only returned on creation. Clients MUST store this — it can't be recovered. */
  plaintext: z.string(),
})
export type CreateApiTokenResponse = z.infer<typeof createApiTokenResponseSchema>

// ── Application API Tokens ─────────────────────────────────
// Admin-managed service credentials. Unlike PATs these are not bound to a
// user — they're identified by name + scopes and authenticate via the same
// `x-api-token` header but use the `cba_` prefix so support/logs can tell
// them apart.

export const applicationApiTokenSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  prefix: z.string(),
  scopes: z.array(scopeSchema),
  createdAt: timestampSchema,
  createdByUserId: z.string().nullable(),
  lastUsedAt: timestampSchema.nullable(),
  expiresAt: timestampSchema.nullable(),
})
export type ApplicationApiTokenSummary = z.infer<typeof applicationApiTokenSummarySchema>

export const createApplicationApiTokenInputSchema = z.object({
  name: z.string().min(1),
  // Zod v3 doesn't have .nonempty on arrays of enums via .array(enum), so we
  // enforce minLength directly. Keeping at least one scope prevents minting
  // a credential that can't actually do anything.
  scopes: z.array(scopeSchema).min(1),
  expiresAt: timestampSchema.nullable().optional(),
})
export type CreateApplicationApiTokenInput = z.infer<
  typeof createApplicationApiTokenInputSchema
>

export const createApplicationApiTokenResponseSchema = z.object({
  id: z.string(),
  name: z.string(),
  prefix: z.string(),
  scopes: z.array(scopeSchema),
  createdAt: timestampSchema,
  expiresAt: timestampSchema.nullable(),
  /** Only returned on creation. Clients MUST store this — it can't be recovered. */
  plaintext: z.string(),
})
export type CreateApplicationApiTokenResponse = z.infer<
  typeof createApplicationApiTokenResponseSchema
>
